import { supabase } from '../lib/supabase'
import { normalizeGameTimeMs } from '../utils/marketingGameTime'
import type {
  ClippingIaAnalysisConfig,
  ClippingSearchSitePurpose,
  CompleteMarketingGameSessionPayload,
  CreateClippingIaAnalysisConfigPayload,
  CreateClippingSearchConfigPayload,
  CreateClippingSearchSitePayload,
  CreateMarketingCatalogPayload,
  CreateMarketingClippingPayload,
  CreateMarketingGamePayload,
  CreateMarketingGameSessionPayload,
  AbandonMarketingGameSessionPayload,
  MarketingClipping,
  MarketingClippingCategory,
  MarketingClippingItemInput,
  MarketingClippingSearchConfig,
  MarketingClippingSearchSite,
  MarketingClippingVehicle,
  MarketingGame,
  MarketingGameQuestion,
  MarketingGameSession,
  MarketingIaAnalysis,
  MarketingIaAnalysisEntityType,
  MarketingNewsAlert,
  MarketingNewsAlertFilters,
  MarketingNewsAlertTriageStatus,
  NewsAlertTriageCounts,
  PromoteNewsAlertPayload,
  UpdateClippingIaAnalysisConfigPayload,
  UpdateClippingSearchConfigPayload,
  UpdateClippingSearchSitePayload,
  UpdateMarketingCatalogPayload,
  UpdateMarketingClippingPayload,
  UpdateMarketingGamePayload,
  MarketingGameChampion,
} from '../types/marketing'

function throwMarketingError(error: unknown): never {
  if (error && typeof error === 'object') {
    const e = error as { message?: string; details?: string; hint?: string; code?: string }
    const parts = [e.message, e.details, e.hint].filter(
      (part): part is string => typeof part === 'string' && part.trim().length > 0
    )
    if (parts.length > 0) {
      throw new Error(parts.join(' — '))
    }
  }
  if (error instanceof Error) throw error
  throw new Error('Erro inesperado na operação de marketing')
}

async function hydrateSearchConfig(
  row: MarketingClippingSearchConfig
): Promise<MarketingClippingSearchConfig> {
  if (!row.default_category_id) {
    return { ...row, default_category: null }
  }
  const { data, error } = await supabase
    .from('marketing_clipping_categories')
    .select('*')
    .eq('id', row.default_category_id)
    .maybeSingle()
  if (error) throwMarketingError(error)
  return {
    ...row,
    default_category: (data as MarketingClippingCategory | null) || null,
  }
}

const CLIPPING_SELECT = `
  *,
  category:category_id(*),
  search_config:search_config_id(id, name),
  items:marketing_clipping_items(
    *,
    vehicle:vehicle_id(*)
  )
`

const NEWS_ALERT_SELECT = `
  *,
  search_site:search_site_id(
    *,
    vehicle:vehicle_id(*)
  ),
  search_config:search_config_id(id, name)
`

const IA_ANALYSIS_SELECT = `
  *,
  suggested_category:suggested_category_id(*)
`

async function listIaAnalysesByEntities(
  entityType: MarketingIaAnalysisEntityType,
  entityIds: string[]
): Promise<Map<string, MarketingIaAnalysis>> {
  const map = new Map<string, MarketingIaAnalysis>()
  if (entityIds.length === 0) return map

  const { data, error } = await supabase
    .from('marketing_ia_analyses')
    .select(IA_ANALYSIS_SELECT)
    .eq('entity_type', entityType)
    .in('entity_id', entityIds)
  if (error) throwMarketingError(error)

  for (const row of data || []) {
    const analysis = row as MarketingIaAnalysis
    map.set(analysis.entity_id, analysis)
  }
  return map
}

async function attachAnalysesToAlerts(
  alerts: MarketingNewsAlert[]
): Promise<MarketingNewsAlert[]> {
  const map = await listIaAnalysesByEntities(
    'alerta',
    alerts.map((item) => item.id)
  )
  return alerts.map((alert) => ({
    ...alert,
    analysis: map.get(alert.id) ?? null,
  }))
}

async function attachAnalysesToClippings(
  clippings: MarketingClipping[]
): Promise<MarketingClipping[]> {
  const map = await listIaAnalysesByEntities(
    'clipping',
    clippings.map((item) => item.id)
  )
  return clippings.map((clipping) => ({
    ...clipping,
    analysis: map.get(clipping.id) ?? null,
  }))
}

function sortNewsAlertsByAnalysis(alerts: MarketingNewsAlert[]): MarketingNewsAlert[] {
  return [...alerts].sort((a, b) => {
    const scoreA = a.analysis?.relevance_score
    const scoreB = b.analysis?.relevance_score
    if (scoreA != null && scoreB != null && scoreA !== scoreB) return scoreB - scoreA
    if (scoreA != null && scoreB == null) return -1
    if (scoreA == null && scoreB != null) return 1

    const publishedA = a.published_at ? new Date(a.published_at).getTime() : 0
    const publishedB = b.published_at ? new Date(b.published_at).getTime() : 0
    if (publishedA !== publishedB) return publishedB - publishedA

    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  })
}

function filterNewsAlertsByAnalysis(
  alerts: MarketingNewsAlert[],
  filters?: MarketingNewsAlertFilters
): MarketingNewsAlert[] {
  let result = alerts

  if (filters?.ia_is_clipping === true || filters?.ia_is_clipping === false) {
    result = result.filter((item) => item.analysis?.is_clipping === filters.ia_is_clipping)
  }

  if (filters?.min_score != null && filters.min_score > 0) {
    const minScore = filters.min_score
    result = result.filter(
      (item) => item.analysis?.relevance_score != null && item.analysis.relevance_score >= minScore
    )
  }

  if (filters?.search?.trim()) {
    const term = filters.search.trim().toLowerCase()
    result = result.filter((item) => {
      const title = item.title?.toLowerCase() || ''
      const snippet = item.snippet?.toLowerCase() || ''
      const summary = item.analysis?.summary?.toLowerCase() || ''
      return title.includes(term) || snippet.includes(term) || summary.includes(term)
    })
  }

  return result
}

const SEARCH_SITE_SELECT = `
  *,
  vehicle:vehicle_id(*)
`

const EMPTY_TRIAGE_COUNTS: NewsAlertTriageCounts = {
  novo: 0,
  analisado: 0,
  promovido: 0,
  descartado: 0,
}

async function replaceClippingItems(
  clippingId: string,
  items: MarketingClippingItemInput[]
): Promise<void> {
  const { error: deleteError } = await supabase
    .from('marketing_clipping_items')
    .delete()
    .eq('clipping_id', clippingId)
  if (deleteError) throw deleteError

  if (items.length === 0) return

  const { error: insertError } = await supabase.from('marketing_clipping_items').insert(
    items.map((item) => ({
      clipping_id: clippingId,
      vehicle_id: item.vehicle_id,
      link: item.link.trim(),
    }))
  )
  if (insertError) throw insertError
}

export const marketingService = {
  async listClippings(): Promise<MarketingClipping[]> {
    const { data, error } = await supabase
      .from('marketing_clippings')
      .select(CLIPPING_SELECT)
      .is('deleted_at', null)
      .order('clipping_date', { ascending: false })
      .order('created_at', { ascending: false })
    if (error) throw error
    return attachAnalysesToClippings((data || []) as MarketingClipping[])
  },

  async createClipping(payload: CreateMarketingClippingPayload): Promise<MarketingClipping> {
    const { data, error } = await supabase
      .from('marketing_clippings')
      .insert({
        title: payload.title,
        clipping_date: payload.clipping_date,
        category_id: payload.category_id,
        notes: payload.notes?.trim() || null,
        origin: payload.origin ?? 'manual',
        source_alert_id: payload.source_alert_id ?? null,
        search_config_id: payload.search_config_id ?? null,
        created_by: payload.created_by ?? null,
      })
      .select('id')
      .single()
    if (error) throw error

    try {
      await replaceClippingItems(data.id, payload.items)
    } catch (itemsError) {
      await supabase.from('marketing_clippings').delete().eq('id', data.id)
      throw itemsError
    }

    const { data: full, error: fetchError } = await supabase
      .from('marketing_clippings')
      .select(CLIPPING_SELECT)
      .eq('id', data.id)
      .single()
    if (fetchError) throw fetchError
    return full as MarketingClipping
  },

  async updateClipping(payload: UpdateMarketingClippingPayload): Promise<MarketingClipping> {
    const { id, items, ...rest } = payload
    const updateData = {
      ...rest,
      ...(rest.notes !== undefined ? { notes: rest.notes?.trim() || null } : {}),
    }
    const { error } = await supabase
      .from('marketing_clippings')
      .update(updateData)
      .eq('id', id)
      .is('deleted_at', null)
    if (error) throw error

    if (items) {
      await replaceClippingItems(id, items)
    }

    const { data, error: fetchError } = await supabase
      .from('marketing_clippings')
      .select(CLIPPING_SELECT)
      .eq('id', id)
      .is('deleted_at', null)
      .single()
    if (fetchError) throw fetchError
    return data as MarketingClipping
  },

  async deleteClipping(id: string, deletedBy?: string | null): Promise<void> {
    const { error } = await supabase
      .from('marketing_clippings')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: deletedBy ?? null,
      })
      .eq('id', id)
      .is('deleted_at', null)
    if (error) throw error
  },

  async listVehicles(activeOnly = true): Promise<MarketingClippingVehicle[]> {
    let query = supabase
      .from('marketing_clipping_vehicles')
      .select('*')
      .order('name', { ascending: true })
    if (activeOnly) query = query.eq('is_active', true)
    const { data, error } = await query
    if (error) throw error
    return (data || []) as MarketingClippingVehicle[]
  },

  async createVehicle(payload: CreateMarketingCatalogPayload): Promise<MarketingClippingVehicle> {
    const { data, error } = await supabase
      .from('marketing_clipping_vehicles')
      .insert({
        name: payload.name.trim(),
        is_active: payload.is_active ?? true,
      })
      .select('*')
      .single()
    if (error) throw error
    return data as MarketingClippingVehicle
  },

  async updateVehicle(id: string, payload: UpdateMarketingCatalogPayload): Promise<MarketingClippingVehicle> {
    const { data, error } = await supabase
      .from('marketing_clipping_vehicles')
      .update({
        ...(payload.name !== undefined ? { name: payload.name.trim() } : {}),
        ...(payload.is_active !== undefined ? { is_active: payload.is_active } : {}),
      })
      .eq('id', id)
      .select('*')
      .single()
    if (error) throw error
    return data as MarketingClippingVehicle
  },

  async listCategories(activeOnly = true): Promise<MarketingClippingCategory[]> {
    let query = supabase
      .from('marketing_clipping_categories')
      .select('*')
      .order('name', { ascending: true })
    if (activeOnly) query = query.eq('is_active', true)
    const { data, error } = await query
    if (error) throw error
    return (data || []) as MarketingClippingCategory[]
  },

  async createCategory(payload: CreateMarketingCatalogPayload): Promise<MarketingClippingCategory> {
    const { data, error } = await supabase
      .from('marketing_clipping_categories')
      .insert({
        name: payload.name.trim(),
        is_active: payload.is_active ?? true,
      })
      .select('*')
      .single()
    if (error) throw error
    return data as MarketingClippingCategory
  },

  async updateCategory(id: string, payload: UpdateMarketingCatalogPayload): Promise<MarketingClippingCategory> {
    const { data, error } = await supabase
      .from('marketing_clipping_categories')
      .update({
        ...(payload.name !== undefined ? { name: payload.name.trim() } : {}),
        ...(payload.is_active !== undefined ? { is_active: payload.is_active } : {}),
      })
      .eq('id', id)
      .select('*')
      .single()
    if (error) throw error
    return data as MarketingClippingCategory
  },

  async listSearchSites(
    activeOnly = false,
    purpose?: ClippingSearchSitePurpose
  ): Promise<MarketingClippingSearchSite[]> {
    let query = supabase
      .from('marketing_search_sites')
      .select(SEARCH_SITE_SELECT)
      .order('name', { ascending: true })
    if (activeOnly) query = query.eq('is_active', true)
    if (purpose) query = query.eq('purpose', purpose)
    const { data, error } = await query
    if (error) throwMarketingError(error)
    return (data || []) as MarketingClippingSearchSite[]
  },

  async createSearchSite(payload: CreateClippingSearchSitePayload): Promise<MarketingClippingSearchSite> {
    const { data, error } = await supabase
      .from('marketing_search_sites')
      .insert({
        name: payload.name.trim(),
        url: payload.url.trim(),
        collector_type: payload.collector_type,
        purpose: payload.purpose,
        vehicle_id: payload.vehicle_id || null,
        notes: payload.notes?.trim() || null,
        is_active: true,
      })
      .select(SEARCH_SITE_SELECT)
      .single()
    if (error) throwMarketingError(error)
    return data as MarketingClippingSearchSite
  },

  async updateSearchSite(
    id: string,
    payload: UpdateClippingSearchSitePayload
  ): Promise<MarketingClippingSearchSite> {
    const { data, error } = await supabase
      .from('marketing_search_sites')
      .update({
        ...(payload.name !== undefined ? { name: payload.name.trim() } : {}),
        ...(payload.url !== undefined ? { url: payload.url.trim() } : {}),
        ...(payload.collector_type !== undefined ? { collector_type: payload.collector_type } : {}),
        ...(payload.purpose !== undefined ? { purpose: payload.purpose } : {}),
        ...(payload.vehicle_id !== undefined ? { vehicle_id: payload.vehicle_id || null } : {}),
        ...(payload.is_active !== undefined ? { is_active: payload.is_active } : {}),
        ...(payload.notes !== undefined ? { notes: payload.notes?.trim() || null } : {}),
      })
      .eq('id', id)
      .select(SEARCH_SITE_SELECT)
      .single()
    if (error) throwMarketingError(error)
    return data as MarketingClippingSearchSite
  },

  async listSearchConfigs(): Promise<MarketingClippingSearchConfig[]> {
    const { data, error } = await supabase
      .from('marketing_clipping_search_configs')
      .select('*')
      .order('name', { ascending: true })
    if (error) throwMarketingError(error)
    const rows = (data || []) as MarketingClippingSearchConfig[]
    return Promise.all(rows.map((row) => hydrateSearchConfig(row)))
  },

  async createSearchConfig(
    payload: CreateClippingSearchConfigPayload
  ): Promise<MarketingClippingSearchConfig> {
    const keywords = payload.keywords.map((k) => k.trim()).filter(Boolean)
    if (keywords.length === 0) {
      throw new Error('Adicione ao menos uma palavra-chave')
    }

    const { data, error } = await supabase
      .from('marketing_clipping_search_configs')
      .insert({
        name: payload.name.trim(),
        keywords,
        keyword_operator: payload.keyword_operator ?? 'or',
        exclude_keywords:
          payload.exclude_keywords && payload.exclude_keywords.length > 0
            ? payload.exclude_keywords.map((k) => k.trim()).filter(Boolean)
            : null,
        default_category_id: payload.default_category_id || null,
        notes: payload.notes?.trim() || null,
        is_active: payload.is_active ?? true,
      })
      .select('*')
      .single()
    if (error) throwMarketingError(error)
    return hydrateSearchConfig(data as MarketingClippingSearchConfig)
  },

  async updateSearchConfig(
    payload: UpdateClippingSearchConfigPayload
  ): Promise<MarketingClippingSearchConfig> {
    const { id, ...rest } = payload
    const updateData: Record<string, unknown> = {}
    if (rest.name !== undefined) updateData.name = rest.name.trim()
    if (rest.keywords !== undefined) {
      const keywords = rest.keywords.map((k) => k.trim()).filter(Boolean)
      if (keywords.length === 0) {
        throw new Error('Adicione ao menos uma palavra-chave')
      }
      updateData.keywords = keywords
    }
    if (rest.keyword_operator !== undefined) updateData.keyword_operator = rest.keyword_operator
    if (rest.exclude_keywords !== undefined) {
      updateData.exclude_keywords =
        rest.exclude_keywords.length > 0
          ? rest.exclude_keywords.map((k) => k.trim()).filter(Boolean)
          : null
    }
    if (rest.default_category_id !== undefined) {
      updateData.default_category_id = rest.default_category_id || null
    }
    if (rest.notes !== undefined) updateData.notes = rest.notes?.trim() || null
    if (rest.is_active !== undefined) updateData.is_active = rest.is_active

    const { data, error } = await supabase
      .from('marketing_clipping_search_configs')
      .update(updateData)
      .eq('id', id)
      .select('*')
      .single()
    if (error) throwMarketingError(error)
    return hydrateSearchConfig(data as MarketingClippingSearchConfig)
  },

  async deleteSearchConfig(id: string): Promise<void> {
    const { error } = await supabase.from('marketing_clipping_search_configs').delete().eq('id', id)
    if (error) throwMarketingError(error)
  },

  async listIaConfigs(): Promise<ClippingIaAnalysisConfig[]> {
    const { data, error } = await supabase
      .from('marketing_ia_configs')
      .select('*')
      .order('name', { ascending: true })
    if (error) throwMarketingError(error)
    return (data || []) as ClippingIaAnalysisConfig[]
  },

  async createIaConfig(
    payload: CreateClippingIaAnalysisConfigPayload
  ): Promise<ClippingIaAnalysisConfig> {
    const { data, error } = await supabase
      .from('marketing_ia_configs')
      .insert({
        name: payload.name.trim(),
        is_active: payload.is_active ?? true,
        topics_of_interest: (payload.topics_of_interest || []).map((k) => k.trim()).filter(Boolean),
        relevance_keywords: (payload.relevance_keywords || []).map((k) => k.trim()).filter(Boolean),
        irrelevance_keywords: (payload.irrelevance_keywords || [])
          .map((k) => k.trim())
          .filter(Boolean),
        analyze_sentiment: payload.analyze_sentiment ?? true,
        generate_summary: payload.generate_summary ?? false,
        summary_length: payload.summary_length ?? 'medium',
        urgency_keywords: (payload.urgency_keywords || []).map((k) => k.trim()).filter(Boolean),
        notes: payload.notes?.trim() || null,
      })
      .select('*')
      .single()
    if (error) throwMarketingError(error)
    return data as ClippingIaAnalysisConfig
  },

  async updateIaConfig(
    payload: UpdateClippingIaAnalysisConfigPayload
  ): Promise<ClippingIaAnalysisConfig> {
    const { id, ...rest } = payload
    const updateData: Record<string, unknown> = {}
    if (rest.name !== undefined) updateData.name = rest.name.trim()
    if (rest.is_active !== undefined) updateData.is_active = rest.is_active
    if (rest.topics_of_interest !== undefined) {
      updateData.topics_of_interest = rest.topics_of_interest.map((k) => k.trim()).filter(Boolean)
    }
    if (rest.relevance_keywords !== undefined) {
      updateData.relevance_keywords = rest.relevance_keywords.map((k) => k.trim()).filter(Boolean)
    }
    if (rest.irrelevance_keywords !== undefined) {
      updateData.irrelevance_keywords = rest.irrelevance_keywords
        .map((k) => k.trim())
        .filter(Boolean)
    }
    if (rest.analyze_sentiment !== undefined) updateData.analyze_sentiment = rest.analyze_sentiment
    if (rest.generate_summary !== undefined) updateData.generate_summary = rest.generate_summary
    if (rest.summary_length !== undefined) updateData.summary_length = rest.summary_length
    if (rest.urgency_keywords !== undefined) {
      updateData.urgency_keywords = rest.urgency_keywords.map((k) => k.trim()).filter(Boolean)
    }
    if (rest.notes !== undefined) updateData.notes = rest.notes?.trim() || null

    const { data, error } = await supabase
      .from('marketing_ia_configs')
      .update(updateData)
      .eq('id', id)
      .select('*')
      .single()
    if (error) throwMarketingError(error)
    return data as ClippingIaAnalysisConfig
  },

  async deleteIaConfig(id: string): Promise<void> {
    const { error } = await supabase.from('marketing_ia_configs').delete().eq('id', id)
    if (error) throwMarketingError(error)
  },

  async listNewsAlerts(filters?: MarketingNewsAlertFilters): Promise<MarketingNewsAlert[]> {
    let query = supabase
      .from('marketing_news_alerts')
      .select(NEWS_ALERT_SELECT)
      .is('deleted_at', null)
      .order('published_at', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false })

    if (filters?.triage_status && filters.triage_status.length > 0) {
      query = query.in('triage_status', filters.triage_status)
    }
    if (filters?.published_from) {
      query = query.gte('published_at', filters.published_from)
    }
    if (filters?.published_to) {
      query = query.lte('published_at', filters.published_to)
    }
    if (filters?.search_site_id) {
      query = query.eq('search_site_id', filters.search_site_id)
    }
    if (filters?.search_config_id) {
      query = query.eq('search_config_id', filters.search_config_id)
    }
    // Busca em título/snippet no servidor; resumo da IA é filtrado após o merge
    if (filters?.search?.trim()) {
      const term = filters.search.trim()
      query = query.or(`title.ilike.%${term}%,snippet.ilike.%${term}%`)
    }

    const { data, error } = await query
    if (error) throw error

    const withAnalysis = await attachAnalysesToAlerts((data || []) as MarketingNewsAlert[])
    // Reaplica busca incluindo summary; filtros de score / is_clipping após merge
    const filtered = filterNewsAlertsByAnalysis(withAnalysis, {
      ...filters,
      // se já filtrou título/snippet no servidor, ainda precisa incluir quem só bate no summary
      search: filters?.search,
    })

    // Se a busca no servidor já restringiu, resultados só com summary matching e sem title/snippet
    // ficariam de fora. Busca complementar por IDs cujo summary bate.
    if (filters?.search?.trim()) {
      const term = filters.search.trim()
      const { data: analysisHits, error: analysisError } = await supabase
        .from('marketing_ia_analyses')
        .select('entity_id')
        .eq('entity_type', 'alerta')
        .ilike('summary', `%${term}%`)
      if (analysisError) throwMarketingError(analysisError)

      const existingIds = new Set(filtered.map((item) => item.id))
      const missingIds = (analysisHits || [])
        .map((row) => row.entity_id as string)
        .filter((id) => !existingIds.has(id))

      if (missingIds.length > 0) {
        let extraQuery = supabase
          .from('marketing_news_alerts')
          .select(NEWS_ALERT_SELECT)
          .is('deleted_at', null)
          .in('id', missingIds)

        if (filters?.triage_status && filters.triage_status.length > 0) {
          extraQuery = extraQuery.in('triage_status', filters.triage_status)
        }
        if (filters?.published_from) {
          extraQuery = extraQuery.gte('published_at', filters.published_from)
        }
        if (filters?.published_to) {
          extraQuery = extraQuery.lte('published_at', filters.published_to)
        }
        if (filters?.search_site_id) {
          extraQuery = extraQuery.eq('search_site_id', filters.search_site_id)
        }
        if (filters?.search_config_id) {
          extraQuery = extraQuery.eq('search_config_id', filters.search_config_id)
        }

        const { data: extraData, error: extraError } = await extraQuery
        if (extraError) throw extraError

        const extraWithAnalysis = await attachAnalysesToAlerts(
          (extraData || []) as MarketingNewsAlert[]
        )
        const extraFiltered = filterNewsAlertsByAnalysis(extraWithAnalysis, {
          ia_is_clipping: filters.ia_is_clipping,
          min_score: filters.min_score,
        })
        return sortNewsAlertsByAnalysis([...filtered, ...extraFiltered])
      }
    }

    return sortNewsAlertsByAnalysis(filtered)
  },

  async countNewsAlertsByTriage(): Promise<NewsAlertTriageCounts> {
    const { data, error } = await supabase
      .from('marketing_news_alerts')
      .select('triage_status')
      .is('deleted_at', null)
    if (error) throw error

    const counts: NewsAlertTriageCounts = { ...EMPTY_TRIAGE_COUNTS }
    for (const row of data || []) {
      const status = row.triage_status as MarketingNewsAlertTriageStatus
      if (status in counts) counts[status] += 1
    }
    return counts
  },

  async discardNewsAlert(id: string, userId?: string | null): Promise<MarketingNewsAlert> {
    const { data, error } = await supabase
      .from('marketing_news_alerts')
      .update({
        triage_status: 'descartado',
        reviewed_by: userId ?? null,
        reviewed_at: new Date().toISOString(),
      })
      .eq('id', id)
      .is('deleted_at', null)
      .select(NEWS_ALERT_SELECT)
      .single()
    if (error) throw error
    const [alert] = await attachAnalysesToAlerts([data as MarketingNewsAlert])
    return alert
  },

  async discardNewsAlerts(ids: string[], userId?: string | null): Promise<void> {
    if (ids.length === 0) return
    const { error } = await supabase
      .from('marketing_news_alerts')
      .update({
        triage_status: 'descartado',
        reviewed_by: userId ?? null,
        reviewed_at: new Date().toISOString(),
      })
      .in('id', ids)
      .is('deleted_at', null)
    if (error) throw error
  },

  async reopenNewsAlert(id: string): Promise<MarketingNewsAlert> {
    const { data, error } = await supabase
      .from('marketing_news_alerts')
      .update({
        triage_status: 'analisado',
        reviewed_by: null,
        reviewed_at: null,
      })
      .eq('id', id)
      .is('deleted_at', null)
      .select(NEWS_ALERT_SELECT)
      .single()
    if (error) throw error
    const [alert] = await attachAnalysesToAlerts([data as MarketingNewsAlert])
    return alert
  },

  async deleteNewsAlert(id: string, deletedBy?: string | null): Promise<void> {
    const { error } = await supabase
      .from('marketing_news_alerts')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: deletedBy ?? null,
      })
      .eq('id', id)
      .is('deleted_at', null)
    if (error) throw error
  },

  async promoteNewsAlertToClipping(
    payload: PromoteNewsAlertPayload
  ): Promise<{ clipping: MarketingClipping; alert: MarketingNewsAlert }> {
    const clipping = await this.createClipping({
      title: payload.title,
      clipping_date: payload.clipping_date,
      category_id: payload.category_id,
      notes: payload.notes,
      origin: 'ia',
      source_alert_id: payload.alert_id,
      search_config_id: payload.search_config_id ?? null,
      created_by: payload.created_by ?? null,
      items: payload.items,
    })

    try {
      const { data, error } = await supabase
        .from('marketing_news_alerts')
        .update({
          triage_status: 'promovido',
          clipping_id: clipping.id,
          reviewed_by: payload.created_by ?? null,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', payload.alert_id)
        .is('deleted_at', null)
        .select(NEWS_ALERT_SELECT)
        .single()
      if (error) throw error
      const [alert] = await attachAnalysesToAlerts([data as MarketingNewsAlert])
      return { clipping, alert }
    } catch (alertError) {
      await supabase.from('marketing_clipping_items').delete().eq('clipping_id', clipping.id)
      await supabase.from('marketing_clippings').delete().eq('id', clipping.id)
      throw alertError
    }
  },

  // =============================================
  // Caça-Palavras (Game)
  // =============================================

  async listGames(): Promise<MarketingGame[]> {
    const { data, error } = await supabase
      .from('marketing_games')
      .select('*')
      .order('created_at', { ascending: false })
    if (error) throwMarketingError(error)
    return ((data ?? []) as MarketingGame[]).map(normalizeGame)
  },

  async hasGames(): Promise<boolean> {
    const { data, error } = await supabase
      .from('marketing_games')
      .select('id')
      .limit(1)
    if (error) throwMarketingError(error)
    return (data ?? []).length > 0
  },

  async getActiveGame(today = new Date().toISOString().slice(0, 10)): Promise<MarketingGame | null> {
    const { data, error } = await supabase
      .from('marketing_games')
      .select('*')
      .eq('is_active', true)
      .lte('start_date', today)
      .gte('end_date', today)
      .order('start_date', { ascending: false })
      .limit(1)
      .maybeSingle()
    if (error) throwMarketingError(error)
    return data ? normalizeGame(data as MarketingGame) : null
  },

  async getGameById(id: string): Promise<MarketingGame | null> {
    const { data, error } = await supabase
      .from('marketing_games')
      .select('*')
      .eq('id', id)
      .maybeSingle()
    if (error) throwMarketingError(error)
    return data ? normalizeGame(data as MarketingGame) : null
  },

  async createGame(payload: CreateMarketingGamePayload): Promise<MarketingGame> {
    const questions = normalizeQuestions(payload.questions)
    const { data, error } = await supabase
      .from('marketing_games')
      .insert({
        title: payload.title.trim(),
        description: payload.description?.trim() || null,
        start_date: payload.start_date,
        end_date: payload.end_date,
        questions,
        is_active: payload.is_active ?? true,
        created_by: payload.created_by ?? null,
      })
      .select('*')
      .single()
    if (error) throwMarketingError(error)
    return normalizeGame(data as MarketingGame)
  },

  async updateGame(id: string, payload: UpdateMarketingGamePayload): Promise<MarketingGame> {
    const update: Record<string, unknown> = {}
    if (payload.title !== undefined) update.title = payload.title.trim()
    if (payload.description !== undefined) update.description = payload.description?.trim() || null
    if (payload.start_date !== undefined) update.start_date = payload.start_date
    if (payload.end_date !== undefined) update.end_date = payload.end_date
    if (payload.is_active !== undefined) update.is_active = payload.is_active
    if (payload.questions !== undefined) update.questions = normalizeQuestions(payload.questions)

    const { data, error } = await supabase
      .from('marketing_games')
      .update(update)
      .eq('id', id)
      .select('*')
      .single()
    if (error) throwMarketingError(error)
    return normalizeGame(data as MarketingGame)
  },

  async deleteGame(id: string): Promise<void> {
    const { error } = await supabase.from('marketing_games').delete().eq('id', id)
    if (error) throwMarketingError(error)
  },

  async getUserGameSession(
    gameId: string,
    userId: string
  ): Promise<MarketingGameSession | null> {
    const { data, error } = await supabase
      .from('marketing_game_sessions')
      .select('*')
      .eq('game_id', gameId)
      .eq('user_id', userId)
      .maybeSingle()
    if (error) throwMarketingError(error)
    return data ? normalizeSession(data as MarketingGameSession) : null
  },

  async createGameSession(
    payload: CreateMarketingGameSessionPayload
  ): Promise<MarketingGameSession> {
    const existing = await this.getUserGameSession(payload.game_id, payload.user_id)
    if (existing) {
      throw new Error('Você já jogou este caça-palavras. Cada usuário só pode jogar uma vez.')
    }

    const { data, error } = await supabase
      .from('marketing_game_sessions')
      .insert({
        game_id: payload.game_id,
        user_id: payload.user_id,
        user_name: payload.user_name.trim() || 'Jogador',
        started_at: payload.started_at ?? new Date().toISOString(),
        status: 'in_progress',
      })
      .select('*')
      .single()
    if (error) {
      if ((error as { code?: string }).code === '23505') {
        throw new Error('Você já jogou este caça-palavras. Cada usuário só pode jogar uma vez.')
      }
      throwMarketingError(error)
    }
    return normalizeSession(data as MarketingGameSession)
  },

  async completeGameSession(
    id: string,
    payload: CompleteMarketingGameSessionPayload
  ): Promise<MarketingGameSession> {
    // in_progress ou abandoned: a aba em segundo plano não pode impedir o registro
    const { data, error } = await supabase
      .from('marketing_game_sessions')
      .update({
        completed_at: payload.completed_at,
        time_seconds: payload.time_seconds,
        word_order: payload.word_order,
        status: 'completed',
      })
      .eq('id', id)
      .in('status', ['in_progress', 'abandoned'])
      .select('*')
      .maybeSingle()
    if (error) throwMarketingError(error)
    if (!data) {
      throw new Error('Não foi possível registrar o resultado desta partida.')
    }
    return normalizeSession(data as MarketingGameSession)
  },

  async abandonGameSession(
    id: string,
    payload: AbandonMarketingGameSessionPayload
  ): Promise<MarketingGameSession | null> {
    const { data, error } = await supabase
      .from('marketing_game_sessions')
      .update({
        time_seconds: payload.time_seconds,
        word_order: payload.word_order,
        status: 'abandoned',
        completed_at: null,
      })
      .eq('id', id)
      .eq('status', 'in_progress')
      .select('*')
      .maybeSingle()
    if (error) throwMarketingError(error)
    return data ? normalizeSession(data as MarketingGameSession) : null
  },

  async listGameSessions(gameId: string): Promise<MarketingGameSession[]> {
    const { data, error } = await supabase
      .from('marketing_game_sessions')
      .select('*')
      .eq('game_id', gameId)
      .in('status', ['completed', 'abandoned'])
    if (error) throwMarketingError(error)

    const sessions = ((data ?? []) as MarketingGameSession[]).map(normalizeSession)
    return sessions.sort((a, b) => {
      const aDone = a.status === 'completed'
      const bDone = b.status === 'completed'
      if (aDone && !bDone) return -1
      if (!aDone && bDone) return 1
      if (aDone && bDone) {
        return (a.time_seconds ?? Number.MAX_SAFE_INTEGER) - (b.time_seconds ?? Number.MAX_SAFE_INTEGER)
      }
      // desistências: mais recentes primeiro
      return String(b.started_at).localeCompare(String(a.started_at))
    })
  },

  async listOverallChampions(): Promise<MarketingGameChampion[]> {
    const { data, error } = await supabase
      .from('marketing_game_sessions')
      .select('*')
      .eq('status', 'completed')
    if (error) throwMarketingError(error)

    const sessions = ((data ?? []) as MarketingGameSession[]).map(normalizeSession)
    const byGame = new Map<string, MarketingGameSession[]>()
    for (const session of sessions) {
      const list = byGame.get(session.game_id) ?? []
      list.push(session)
      byGame.set(session.game_id, list)
    }

    const winnersByUser = new Map<
      string,
      {
        user_id: string
        user_name: string
        victories: number
        completions: number
        best_time_seconds: number | null
        games_won: Set<string>
      }
    >()

    const touch = (session: MarketingGameSession) => {
      const current = winnersByUser.get(session.user_id) ?? {
        user_id: session.user_id,
        user_name: session.user_name,
        victories: 0,
        completions: 0,
        best_time_seconds: null as number | null,
        games_won: new Set<string>(),
      }
      current.user_name = session.user_name || current.user_name
      current.completions += 1
      if (
        session.time_seconds != null &&
        (current.best_time_seconds == null || session.time_seconds < current.best_time_seconds)
      ) {
        current.best_time_seconds = session.time_seconds
      }
      winnersByUser.set(session.user_id, current)
    }

    for (const session of sessions) {
      touch(session)
    }

    for (const [gameId, gameSessions] of byGame.entries()) {
      const sorted = [...gameSessions].sort(
        (a, b) =>
          (a.time_seconds ?? Number.MAX_SAFE_INTEGER) - (b.time_seconds ?? Number.MAX_SAFE_INTEGER)
      )
      const winner = sorted[0]
      if (!winner) continue
      const row = winnersByUser.get(winner.user_id)
      if (!row) continue
      row.victories += 1
      row.games_won.add(gameId)
    }

    return Array.from(winnersByUser.values())
      .map((row) => ({
        user_id: row.user_id,
        user_name: row.user_name,
        victories: row.victories,
        completions: row.completions,
        best_time_seconds: row.best_time_seconds,
        games_won: Array.from(row.games_won),
      }))
      .sort((a, b) => {
        if (b.victories !== a.victories) return b.victories - a.victories
        if (b.completions !== a.completions) return b.completions - a.completions
        return (a.best_time_seconds ?? Number.MAX_SAFE_INTEGER) - (b.best_time_seconds ?? Number.MAX_SAFE_INTEGER)
      })
  },
}

function normalizeQuestions(questions: MarketingGameQuestion[]): MarketingGameQuestion[] {
  return questions
    .map((q) => ({
      clue: (q.clue || '').trim(),
      word: normalizeWord(q.word),
    }))
    .filter((q) => q.clue.length > 0 && q.word.length >= 2)
    .slice(0, 12)
}

function normalizeWord(word: string): string {
  return (word || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z]/g, '')
    .toUpperCase()
}

function normalizeGame(row: MarketingGame): MarketingGame {
  const questions = Array.isArray(row.questions) ? row.questions : []
  return {
    ...row,
    questions: questions.map((q) => ({
      clue: String(q?.clue ?? ''),
      word: normalizeWord(String(q?.word ?? '')),
    })),
  }
}

function normalizeSession(row: MarketingGameSession): MarketingGameSession {
  const order = Array.isArray(row.word_order) ? row.word_order : []
  const status =
    row.status === 'completed' || row.status === 'abandoned' || row.status === 'in_progress'
      ? row.status
      : row.completed_at
        ? 'completed'
        : 'abandoned'
  return {
    ...row,
    status,
    // Coluna time_seconds = milissegundos; normaliza legados (segundos / *1000 indevido)
    time_seconds:
      row.time_seconds == null
        ? null
        : normalizeGameTimeMs(row.time_seconds, row.started_at, row.completed_at),
    word_order: order.map((n) => Number(n)).filter((n) => Number.isFinite(n)),
  }
}
